/**
 * PipelineToggleCard — Phase G · Live-Rollback Dashboard
 *
 * Three Radix Switch toggles that mutate the
 * `system_settings.pipeline_flags` document in MongoDB at runtime,
 * letting an operator roll Modal serverless execution forward/back
 * to local-pod execution during a live tournament — no SSH, no .env
 * edit, no supervisor restart.
 *
 * The card lives on the /admin/settings page (Phase 1 dashboard).
 * Each toggle:
 *   • Shows the EFFECTIVE state via the Switch position
 *   • Surfaces the SOURCE (db_override vs env_default) as a
 *     small mono badge under the label, so operators can tell at a
 *     glance whether a flag is on because of a deployment env var
 *     or a runtime hot-swap.
 *   • Flips fire `POST /admin/pipeline-settings` immediately, with
 *     an inline spinner + a Sonner toast on success/failure.
 *   • A "Reset to ENV default" mini-button next to each toggle
 *     clears the DB override without forcing a particular value —
 *     useful when an operator wants the flag's state to track the
 *     deployment again.
 */
import React, { useEffect, useState } from "react";
import { Zap, HardDrive, Loader2, RotateCcw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { fehlerText, getPipelineSettings, updatePipelineSetting } from "@/lib/api";

const PIPELINES = [
  {
    key: "use_modal_rerender",
    label: "Render Pipeline",
    sub: "GPU Cloud · Re-Render Overlay MP4",
    modal_label: "Modal T4",
  },
  {
    key: "use_modal_harvester",
    label: "Harvest Pipeline",
    sub: "CPU Cloud · Pass-Vector Slice",
    modal_label: "Modal CPU",
  },
  {
    key: "use_modal_frame_extractor",
    label: "Frame Extractor Pipeline",
    sub: "CPU Cloud · UI Frame JPGs",
    modal_label: "Modal CPU",
  },
];


function PipelineRow({ pipeline, entry, busy, onChange, onReset }) {
  const isModal = entry?.enabled === true;
  const isOverride = entry?.source === "db_override";
  return (
    <div
      data-testid={`pipeline-toggle-row-${pipeline.key}`}
      data-source={entry?.source}
      className="flex items-center justify-between gap-4 py-3 border-b border-border/40 last:border-b-0"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {isModal ? (
            <Zap className="w-3.5 h-3.5 text-emerald-400" strokeWidth={2.5} />
          ) : (
            <HardDrive className="w-3.5 h-3.5 text-amber-400" strokeWidth={2.5} />
          )}
          <span className="font-display font-bold uppercase tracking-wider text-xs">
            {pipeline.label}
          </span>
        </div>
        <div className="font-mono text-[10px] text-muted-foreground mt-0.5 ml-5">
          {pipeline.sub}
        </div>
        <div className="mt-1 ml-5 flex items-center gap-2">
          <span
            data-testid={`pipeline-toggle-source-${pipeline.key}`}
            className={[
              "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm font-mono text-[9px] uppercase tracking-widest",
              isOverride
                ? "bg-rose-500/15 text-rose-300 border border-rose-500/30"
                : "bg-muted text-muted-foreground border border-border",
            ].join(" ")}
          >
            {isOverride && <ShieldAlert className="w-2.5 h-2.5" />}
            {isOverride ? "DB OVERRIDE" : "ENV DEFAULT"}
          </span>
          <span className="font-mono text-[9px] text-muted-foreground">
            env={String(entry?.env_value)}
            {isOverride && (
              <>
                {" · "}
                <span className="text-rose-300">db={String(entry?.db_value)}</span>
              </>
            )}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {isOverride && (
          <button
            type="button"
            data-testid={`pipeline-toggle-reset-${pipeline.key}`}
            onClick={() => onReset(pipeline.key)}
            disabled={busy}
            title="Clear DB override — revert to environment default"
            className="p-1 rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        )}
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        ) : (
          <Switch
            data-testid={`pipeline-toggle-${pipeline.key}`}
            checked={isModal}
            onCheckedChange={(checked) => onChange(pipeline.key, checked)}
            disabled={busy}
            aria-label={`${pipeline.label} (currently ${isModal ? "Modal" : "Local"})`}
          />
        )}
      </div>
    </div>
  );
}


export default function PipelineToggleCard() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getPipelineSettings();
        if (alive) setSettings(res.settings);
      } catch (err) {
        if (alive) {
          toast.error("Failed to load pipeline settings", {
            description: err?.message || "network error",
          });
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleChange = async (key, enabled) => {
    if (busyKey) return;
    setBusyKey(key);
    try {
      const res = await updatePipelineSetting(key, enabled);
      setSettings(res.settings);
      const labelEntry = PIPELINES.find((p) => p.key === key);
      const dest = enabled ? labelEntry.modal_label : "Local Execution";
      toast.success(
        `🔄 ${labelEntry.label} switched to ${dest}`,
        {
          description: "Hot-swap applied — no restart required.",
          duration: 5_000,
          id: `pipeline-toggle-${key}`,
        },
      );
    } catch (err) {
      toast.error("Pipeline toggle failed", {
        description: fehlerText(err) || err?.message || "unknown error",
        id: `pipeline-toggle-fail-${key}`,
      });
    } finally {
      setBusyKey(null);
    }
  };

  const handleReset = async (key) => {
    if (busyKey) return;
    setBusyKey(key);
    try {
      const res = await updatePipelineSetting(key, null);
      setSettings(res.settings);
      const labelEntry = PIPELINES.find((p) => p.key === key);
      toast.success(
        `↩️ ${labelEntry.label} override cleared`,
        {
          description: "Now tracking environment default.",
          duration: 5_000,
          id: `pipeline-toggle-reset-${key}`,
        },
      );
    } catch (err) {
      toast.error("Reset failed", {
        description: fehlerText(err) || err?.message || "unknown error",
      });
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div
      data-testid="pipeline-toggle-card"
      className="border border-border bg-card rounded-sm p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <Zap className="w-4 h-4 text-primary" />
          <h2 className="font-display font-black uppercase tracking-widest text-sm">
            Pipeline Live-Rollback
          </h2>
        </div>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </div>

      <p className="font-mono text-[10px] text-muted-foreground mb-3 leading-relaxed">
        Hot-swap each media pipeline between Modal serverless and local
        execution. Override applies on the NEXT dispatch — no supervisor
        restart. Use the {" "}
        <RotateCcw className="inline w-2.5 h-2.5 mx-0.5" />
        {" "} button to clear an override and follow the deployment env.
      </p>

      {loading && !settings ? (
        <div className="py-4 font-mono text-xs text-muted-foreground">
          Loading…
        </div>
      ) : (
        <div className="-my-1">
          {PIPELINES.map((p) => (
            <PipelineRow
              key={p.key}
              pipeline={p}
              entry={settings?.[p.key]}
              busy={busyKey === p.key}
              onChange={handleChange}
              onReset={handleReset}
            />
          ))}
        </div>
      )}
    </div>
  );
}
